import { type ReactNode } from "react";
import { type IntegrationType } from "../docs/integrations";
import { trackButtonPressed } from "../../analytics/tracking";
import { SUPPORT_EMAIL } from "./waiting";

/** Collapsed and always there, rather than revealed on a timer: it costs nothing closed,
 *  and a reader who already suspects a mistake should not have to wait for it. */
export function TroubleshootingChecklist({
  integration,
}: {
  integration: IntegrationType;
}) {
  return (
    <details
      className="rounded-[8px] border p-4"
      style={{ borderColor: "var(--onb-track)" }}
    >
      <summary
        className="cursor-pointer text-[13px] font-medium"
        style={{ color: "var(--fg-1)" }}
      >
        Having trouble connecting?
      </summary>
      <ul className="mt-3 flex flex-col gap-2.5 pl-4 text-[11px] text-[var(--text-muted)]">
        {troubleshootingTips(integration)}
      </ul>
    </details>
  );
}

export function SupportLink() {
  return (
    <p className="text-[11px]" style={{ color: "var(--fg-3)" }}>
      Still no traffic?{" "}
      <a
        href={`mailto:${SUPPORT_EMAIL}`}
        onClick={() => trackButtonPressed("Contact us")}
        style={{ color: "var(--onb-link)", fontWeight: 600 }}
      >
        Contact us
      </a>
      .
    </p>
  );
}

/* Integration-specific: Claude Code's settings-file pitfalls don't apply to SDK-configured
 * agents, which get client-construction and base-URL checks instead. */
function troubleshootingTips(integration: IntegrationType): ReactNode {
  if (integration === "claude-code") {
    return (
      <>
        <li>
          Restart any already-open Claude Code sessions —{" "}
          <InlineMono>settings.json</InlineMono> env vars load at process
          startup only.
        </li>
        <li>
          Run <InlineMono>/status</InlineMono> inside Claude Code to see which
          settings files were loaded.
        </li>
        <li>
          Check that <InlineMono>ANTHROPIC_CUSTOM_HEADERS</InlineMono> values
          are newline-separated.
        </li>
      </>
    );
  }

  if (integration === "codex") {
    return (
      <>
        <li>
          Restart any already-open Codex sessions —{" "}
          <InlineMono>config.toml</InlineMono> is read at process startup only.
        </li>
        <li>
          Check <InlineMono>x-upstream: openai-codex</InlineMono> is set — it
          tells the gateway to use your ChatGPT sign-in rather than an OpenAI
          API key.
        </li>
        <li>
          Run <InlineMono>codex login</InlineMono> and check{" "}
          <InlineMono>requires_openai_auth = true</InlineMono> is present.
        </li>
        <li>
          Check <InlineMono>base_url</InlineMono> ends in{" "}
          <InlineMono>/v1</InlineMono> — Codex appends{" "}
          <InlineMono>/responses</InlineMono> itself.
        </li>
      </>
    );
  }

  if (integration === "langchain") {
    return (
      <>
        <li>
          Re-run your agent — the client reads <InlineMono>base_url</InlineMono>{" "}
          and headers once, when it&apos;s constructed.
        </li>
        <li>
          Check <InlineMono>base_url</InlineMono> points at the gateway, not
          directly at the provider.
        </li>
        <li>
          Check the AI21 headers values in{" "}
          <InlineMono>default_headers</InlineMono>.
        </li>
      </>
    );
  }

  return (
    <>
      {/* Path first: it is the one mistake that fails as a 404 rather than as
          anything that names a credential, so it reads as the wrong URL. */}
      <li>
        Check the base URL path — an OpenAI-style client needs{" "}
        <InlineMono>/v1</InlineMono> on the end, an Anthropic one doesn&apos;t.
      </li>
      <li>
        Check every request sends <InlineMono>x-ai21-key</InlineMono> and{" "}
        <InlineMono>x-agent-id</InlineMono>.
      </li>
      <li>Restart your agent so the new base URL and headers take effect.</li>
      <li>
        Check requests go to the gateway URL, not directly to the provider.
      </li>
    </>
  );
}

function InlineMono({ children }: { children: string }) {
  return (
    <code className="rounded bg-surface-muted px-1 py-0.5 font-mono text-[11px]">
      {children}
    </code>
  );
}
